/* ================================================================
   FeesPage.tsx — Module Scolarité (Gestion des frais)
   Date: 2026-08-13
   Corrections:
     - Totaux : conversion Number() des Decimal strings du backend
     - Export PDF : impression stylisée avec entête établissement
     - Export CSV : amélioré avec ligne de totaux
   ================================================================ */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Search, X, Layers, Banknote, FileSpreadsheet,
  Users, School, Filter, Printer,
  Plus, Trash2, Edit3, RefreshCw,
  FileDown, AlertCircle, CheckCircle2, XCircle, Clock,
  Calculator, Wallet, BarChart3,
  Loader2, PieChart
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { useAuthStore } from '@/store/authStore'
import { schoolApi, classesApi, studentsApi, feesApi, cyclesApi, levelsApi } from '@/services/api'
import toast from 'react-hot-toast'

// ─── Interfaces ───
interface Section { id: number; code: string; label_fr: string; label_en: string; school_id?: number }
interface Cycle { id: number; section_id: number; code: string; label_fr: string; label_en: string; order: number }
interface Level { id: number; cycle_id: number; code: string; label_fr: string; label_en: string; order: number }
interface ClassItem { id: number; level_id: number; specialty_id?: number; code: string; abbreviation: string; name: string; capacity?: number; status?: string }

interface Student { id: number; first_name: string; last_name: string; matricule?: string; gender?: string }
interface FeeType { id: number; name: string; code: string; description?: string; category?: string; institution_type: string; display_order: number; is_mandatory?: boolean; is_active?: boolean }
interface FeeStructure { id: number; fee_type_id: number; amount: number; class_id?: number; level_id?: number; academic_year_id?: number; due_date?: string; notes?: string; is_active?: boolean; fee_type?: FeeType }
interface PaymentStatus {
  student_id: number; student_name: string; matricule?: string; gender?: string;
  class_name: string; level_name: string; section_name?: string;
  fee_type_id: number; fee_type_name: string;
  fee_type_code: string; fee_structure_id: number;
  amount_due: number; amount_paid: number; remaining: number; status: string;
  payment_date?: string; receipt_number?: string;
}

// ─── Traductions ───
const t = {
  fr: {
    title: 'Scolarité',
    subtitle: 'Gestion des frais scolaires — Paiements & Suivi',
    tabPayments: 'Paiements',
    tabTypes: 'Types de frais',
    tabStructures: 'Structures',
    tabReports: 'Rapports',
    institutionType: "Type d'établissement",
    lycee: 'Lycée', college: 'Collège',
    section: 'Section', cycle: 'Cycle', level: 'Niveau', class: 'Classe',
    selectSection: 'Choisir une section', selectCycle: 'Choisir un cycle',
    selectLevel: 'Choisir un niveau', selectClass: 'Choisir une classe',
    searchStudent: 'Rechercher un élève...',
    noData: 'Aucune donnée', loading: 'Chargement...',
    student: 'Élève', matricule: 'Matricule', gender: 'Sexe',
    amountDue: 'Montant dû', amountPaid: 'Payé', remaining: 'Reste', status: 'Statut',
    paid: 'Payé', unpaid: 'Non payé', partial: 'Partiel', exempted: 'Exonéré',
    savePayments: 'Enregistrer les paiements',
    exportPaid: 'Exporter payés (CSV)', exportUnpaid: 'Exporter non-payés (CSV)',
    exportAll: 'Exporter tout (CSV)', exportPDF: 'Imprimer / PDF',
    totalStudents: 'Élèves', totalDue: 'Total dû', totalPaid: 'Total payé',
    totalRemaining: 'Reste global', paymentRate: 'Taux de paiement',
    feeType: 'Type de frais', amount: 'Montant', dueDate: "Date d'échéance",
    actions: 'Actions', add: 'Ajouter', edit: 'Modifier', delete: 'Supprimer',
    confirmDelete: 'Confirmer la suppression ?',
    cancel: 'Annuler', save: 'Enregistrer', close: 'Fermer',
    createFeeType: 'Nouveau type de frais', createStructure: 'Nouvelle structure',
    editFeeType: 'Modifier le type', editStructure: 'Modifier la structure',
    code: 'Code', name: 'Nom', description: 'Description', category: 'Catégorie',
    mandatory: 'Obligatoire', active: 'Actif',
    academicYear: 'Année académique', selectFeeType: 'Choisir un type',
    bulkPaymentTitle: 'Paiement en masse',
    bulkPaymentDesc: 'Appliquer le même statut à tous les élèves sélectionnés pour ce type de frais',
    paymentMethod: 'Mode de paiement',
    cash: 'Espèces', bankTransfer: 'Virement bancaire', mobileMoney: 'Mobile Money', cheque: 'Chèque',
    reference: 'Référence', receipt: 'Reçu N°', notes: 'Notes', apply: 'Appliquer',
    selectAll: 'Tout sélectionner', deselectAll: 'Tout désélectionner',
    paidCount: 'payés', unpaidCount: 'non payés', partialCount: 'partiels',
    refresh: 'Actualiser',
    noStudents: 'Aucun élève dans cette classe',
    noFeeStructures: 'Aucune structure de frais définie pour cette classe',
    pleaseSelectClass: 'Veuillez sélectionner une classe',
    seedDefaults: 'Initialiser les types par défaut', seedSuccess: 'Types de frais initialisés',
    payAllTypes: 'Payer tous les types',
    payAllTypesDesc: 'Valider le paiement de tous les types de frais pour les élèves sélectionnés',
    payAllTypesConfirm: 'Valider le paiement pour tous les types de frais ?',
    payAllTypesSuccess: 'Tous les types payés pour',
    selectedOnly: 'élèves sélectionnés uniquement',
    allVisible: 'tous les élèves visibles',
    summary: 'Résumé', classSummary: 'Résumé de la classe',
    byStudent: 'Par élève', byFeeType: 'Par type de frais', print: 'Imprimer',
    filterByStructure: 'Filtrer par structure académique',
    resetFilters: 'Réinitialiser les filtres',
    activeFilters: 'Filtres actifs:',
    schoolName: 'Lycée Bilingue de Baleng',
    schoolAddress: 'Baleng, Ouest, Cameroun',
    reportTitle: 'RELEVE DE PAIEMENT DES FRAIS SCOLAIRES',
    reportDate: 'Date',
    reportClass: 'Classe',
    reportYear: 'Année académique',
    total: 'TOTAL',
    page: 'Page',
    of: 'sur',
  },
  en: {
    title: 'School Fees',
    subtitle: 'Tuition management — Payments & Tracking',
    tabPayments: 'Payments',
    tabTypes: 'Fee Types',
    tabStructures: 'Structures',
    tabReports: 'Reports',
    institutionType: 'Institution Type',
    lycee: 'High School', college: 'College',
    section: 'Section', cycle: 'Cycle', level: 'Level', class: 'Class',
    selectSection: 'Select a section', selectCycle: 'Select a cycle',
    selectLevel: 'Select a level', selectClass: 'Select a class',
    searchStudent: 'Search student...',
    noData: 'No data', loading: 'Loading...',
    student: 'Student', matricule: 'Registration', gender: 'Gender',
    amountDue: 'Amount due', amountPaid: 'Paid', remaining: 'Remaining', status: 'Status',
    paid: 'Paid', unpaid: 'Unpaid', partial: 'Partial', exempted: 'Exempted',
    savePayments: 'Save payments',
    exportPaid: 'Export paid (CSV)', exportUnpaid: 'Export unpaid (CSV)',
    exportAll: 'Export all (CSV)', exportPDF: 'Print / PDF',
    totalStudents: 'Students', totalDue: 'Total due', totalPaid: 'Total paid',
    totalRemaining: 'Global remaining', paymentRate: 'Payment rate',
    feeType: 'Fee type', amount: 'Amount', dueDate: 'Due date',
    actions: 'Actions', add: 'Add', edit: 'Edit', delete: 'Delete',
    confirmDelete: 'Confirm deletion?',
    cancel: 'Cancel', save: 'Save', close: 'Close',
    createFeeType: 'New fee type', createStructure: 'New structure',
    editFeeType: 'Edit fee type', editStructure: 'Edit structure',
    code: 'Code', name: 'Name', description: 'Description', category: 'Category',
    mandatory: 'Mandatory', active: 'Active',
    academicYear: 'Academic year', selectFeeType: 'Select fee type',
    bulkPaymentTitle: 'Bulk payment',
    bulkPaymentDesc: 'Apply the same status to all selected students for this fee type',
    paymentMethod: 'Payment method',
    cash: 'Cash', bankTransfer: 'Bank transfer', mobileMoney: 'Mobile Money', cheque: 'Cheque',
    reference: 'Reference', receipt: 'Receipt No.', notes: 'Notes', apply: 'Apply',
    selectAll: 'Select all', deselectAll: 'Deselect all',
    paidCount: 'paid', unpaidCount: 'unpaid', partialCount: 'partial',
    refresh: 'Refresh',
    noStudents: 'No students in this class',
    noFeeStructures: 'No fee structure defined for this class',
    pleaseSelectClass: 'Please select a class',
    seedDefaults: 'Initialize default types', seedSuccess: 'Fee types initialized',
    payAllTypes: 'Pay all types',
    payAllTypesDesc: 'Validate payment for all fee types for selected students',
    payAllTypesConfirm: 'Validate payment for all fee types?',
    payAllTypesSuccess: 'All types paid for',
    selectedOnly: 'selected students only',
    allVisible: 'all visible students',
    summary: 'Summary', classSummary: 'Class summary',
    byStudent: 'By student', byFeeType: 'By fee type', print: 'Print',
    filterByStructure: 'Filter by academic structure',
    resetFilters: 'Reset filters',
    activeFilters: 'Active filters:',
    schoolName: 'Lycée Bilingue de Baleng',
    schoolAddress: 'Baleng, West, Cameroon',
    reportTitle: 'SCHOOL FEES PAYMENT STATEMENT',
    reportDate: 'Date',
    reportClass: 'Class',
    reportYear: 'Academic year',
    total: 'TOTAL',
    page: 'Page',
    of: 'of',
  }
}

// ─── Helpers ───
function extractArray(response: any): any[] {
  if (!response) return []
  if (Array.isArray(response)) return response
  const res = response as any
  if (Array.isArray(res.data)) return res.data
  if (Array.isArray(res.items)) return res.items
  if (Array.isArray(res.results)) return res.results
  const possibleKeys = ['data', 'items', 'results', 'students', 'classes', 'levels', 'cycles', 'sections', 'fee_types', 'structures', 'payments']
  for (const key of possibleKeys) {
    if (Array.isArray(res[key])) return res[key]
    if (res.data && Array.isArray(res.data[key])) return res.data[key]
  }
  return []
}

/** Convertit une valeur Decimal/string/number en nombre sûr */
function toNum(v: any): number {
  if (v === undefined || v === null) return 0
  if (typeof v === 'number') return isNaN(v) ? 0 : v
  if (typeof v === 'string') {
    const n = parseFloat(v.replace(/\s/g, '').replace(/,/g, ''))
    return isNaN(n) ? 0 : n
  }
  return 0
}

function formatFCFA(n: number | string) {
  const num = toNum(n)
  return num.toLocaleString('fr-FR') + ' FCFA'
}

function downloadCSV(filename: string, rows: string[][]) {
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n')
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Fee Type
// ═══════════════════════════════════════════════════════════════
function FeeTypeModal({ show, onClose, editingType, institutionType, txt, onSaved }: {
  show: boolean; onClose: () => void; editingType: FeeType | null;
  institutionType: string; txt: any; onSaved: () => void
}) {
  const [form, setForm] = useState({
    name: editingType?.name || '', code: editingType?.code || '',
    description: editingType?.description || '', category: editingType?.category || '',
    display_order: editingType?.display_order || 0,
    is_mandatory: editingType?.is_mandatory ?? true, is_active: editingType?.is_active ?? true,
  })
  useEffect(() => {
    setForm({
      name: editingType?.name || '', code: editingType?.code || '',
      description: editingType?.description || '', category: editingType?.category || '',
      display_order: editingType?.display_order || 0,
      is_mandatory: editingType?.is_mandatory ?? true, is_active: editingType?.is_active ?? true,
    })
  }, [editingType, show])
  const submit = async () => {
    try {
      const data = { ...form, institution_type: institutionType }
      if (editingType) { await feesApi.updateFeeType(editingType.id, data); toast.success('Type modifié') }
      else { await feesApi.createFeeType(data); toast.success('Type créé') }
      onSaved(); onClose()
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Erreur') }
  }
  if (!show) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{editingType ? txt.editFeeType : txt.createFeeType}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.code}</label>
            <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="FRAIS_EXIGIBLES" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.name}</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="Frais exigibles" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.category}</label>
            <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="exigible, examen, photo..." /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.description}</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" rows={2} /></div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.is_mandatory} onChange={e => setForm({ ...form, is_mandatory: e.target.checked })} className="rounded border-gray-300 text-primary-600" />{txt.mandatory}</label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded border-gray-300 text-primary-600" />{txt.active}</label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">{txt.cancel}</button>
          <button onClick={submit} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700">{txt.save}</button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Fee Structure
// ═══════════════════════════════════════════════════════════════
function FeeStructureModal({ show, onClose, editingStructure, feeTypes, levels, classes, selectedLevel, selectedClass, selectedYear, institutionType, userSchoolId, txt, onSaved }: {
  show: boolean; onClose: () => void; editingStructure: FeeStructure | null;
  feeTypes: FeeType[]; levels: Level[]; classes: ClassItem[];
  selectedLevel: number | ''; selectedClass: number | ''; selectedYear: number | '';
  institutionType: string; userSchoolId: number | undefined; txt: any; onSaved: () => void
}) {
  const [form, setForm] = useState({
    fee_type_id: editingStructure?.fee_type_id || '', level_id: editingStructure?.level_id || selectedLevel || '',
    class_id: editingStructure?.class_id || selectedClass || '', amount: editingStructure?.amount || '',
    academic_year_id: editingStructure?.academic_year_id || selectedYear || '',
    due_date: editingStructure?.due_date ? new Date(editingStructure.due_date as any).toISOString().split('T')[0] : '',
    notes: editingStructure?.notes || '', is_active: editingStructure?.is_active ?? true,
  })
  useEffect(() => {
    setForm({
      fee_type_id: editingStructure?.fee_type_id || '', level_id: editingStructure?.level_id || selectedLevel || '',
      class_id: editingStructure?.class_id || selectedClass || '', amount: editingStructure?.amount || '',
      academic_year_id: editingStructure?.academic_year_id || selectedYear || '',
      due_date: editingStructure?.due_date ? new Date(editingStructure.due_date as any).toISOString().split('T')[0] : '',
      notes: editingStructure?.notes || '', is_active: editingStructure?.is_active ?? true,
    })
  }, [editingStructure, show, selectedLevel, selectedClass, selectedYear])
  const submit = async () => {
    if (!userSchoolId) { toast.error('École non identifiée'); return }
    try {
      const data = {
        fee_type_id: Number(form.fee_type_id), level_id: Number(form.level_id) || null,
        class_id: Number(form.class_id) || null, amount: parseFloat(String(form.amount)) || 0,
        academic_year_id: Number(form.academic_year_id), school_id: userSchoolId,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        notes: form.notes, is_active: form.is_active,
      }
      if (editingStructure) { await feesApi.updateFeeStructure(editingStructure.id, data); toast.success('Structure modifiée') }
      else { await feesApi.createFeeStructure(data); toast.success('Structure créée') }
      onSaved(); onClose()
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Erreur') }
  }
  if (!show) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{editingStructure ? txt.editStructure : txt.createStructure}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.feeType}</label>
            <select value={form.fee_type_id} onChange={e => setForm({ ...form, fee_type_id: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">{txt.selectFeeType}</option>
              {feeTypes.filter(ft => ft.institution_type === institutionType).map(ft => (<option key={ft.id} value={ft.id}>{ft.name}</option>))}
            </select></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.level}</label>
              <select value={form.level_id} onChange={e => setForm({ ...form, level_id: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">—</option>{levels.map(l => <option key={l.id} value={l.id}>{l.label_fr}</option>)}</select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.class}</label>
              <select value={form.class_id} onChange={e => setForm({ ...form, class_id: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">—</option>{classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.amount} (FCFA)</label>
            <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="32500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.dueDate}</label>
            <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.notes}</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" rows={2} /></div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">{txt.cancel}</button>
          <button onClick={submit} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700">{txt.save}</button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Bulk Payment
// ═══════════════════════════════════════════════════════════════
function BulkPaymentModal({ show, onClose, selectedStudents, feeTypesForClass, selectedClass, selectedYear, txt, onSaved }: {
  show: boolean; onClose: () => void; selectedStudents: Set<number>;
  feeTypesForClass: FeeType[]; selectedClass: number | ''; selectedYear: number | ''; txt: any; onSaved: () => void
}) {
  const [bulkFeeTypeId, setBulkFeeTypeId] = useState<number | ''>('')
  const [bulkAmount, setBulkAmount] = useState('')
  const [bulkMethod, setBulkMethod] = useState('cash')
  const [bulkReference, setBulkReference] = useState('')
  const [bulkReceipt, setBulkReceipt] = useState('')
  useEffect(() => { if (show) { setBulkFeeTypeId(''); setBulkAmount(''); setBulkMethod('cash'); setBulkReference(''); setBulkReceipt('') } }, [show])
  const handleBulkPayment = async () => {
    if (!selectedClass || !selectedYear || !bulkFeeTypeId || selectedStudents.size === 0) {
      toast.error('Veuillez sélectionner une classe, une année, un type de frais et des élèves'); return
    }
    try {
      await feesApi.createBulkPaymentsByType({
        student_ids: Array.from(selectedStudents), class_id: selectedClass, fee_type_id: bulkFeeTypeId,
        academic_year_id: selectedYear, amount_paid: parseFloat(bulkAmount) || 0,
        payment_method: bulkMethod, status: parseFloat(bulkAmount) > 0 ? 'paid' : 'unpaid', notes: bulkReference || undefined
      })
      toast.success(`${selectedStudents.size} paiements enregistrés`); onSaved(); onClose()
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Erreur lors du paiement en masse') }
  }
  if (!show) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{txt.bulkPaymentTitle}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-500">{txt.bulkPaymentDesc} ({selectedStudents.size} élèves)</p>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.feeType}</label>
            <select value={bulkFeeTypeId} onChange={e => setBulkFeeTypeId(e.target.value ? parseInt(e.target.value) : '')} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="">{txt.selectFeeType}</option>{feeTypesForClass.map(ft => <option key={ft.id} value={ft.id}>{ft.name}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.amount} (FCFA)</label>
            <input type="number" value={bulkAmount} onChange={e => setBulkAmount(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.paymentMethod}</label>
            <select value={bulkMethod} onChange={e => setBulkMethod(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
              <option value="cash">{txt.cash}</option><option value="bank_transfer">{txt.bankTransfer}</option><option value="mobile_money">{txt.mobileMoney}</option><option value="cheque">{txt.cheque}</option></select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.reference}</label>
            <input value={bulkReference} onChange={e => setBulkReference(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">{txt.receipt}</label>
            <input value={bulkReceipt} onChange={e => setBulkReceipt(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" /></div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50">{txt.cancel}</button>
          <button onClick={handleBulkPayment} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700">{txt.apply}</button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function FeesPage() {
  const { lang } = useLang()
  const txt = t[lang]
  const { user, permissions } = useAuthStore()
  const userSchoolId: number | undefined = user?.school_id ? Number(user.school_id) : undefined

  // ─── PERMISSIONS ───
  const rawPerms: string[] = useMemo(() => {
    if (Array.isArray(permissions)) return permissions
    if (typeof permissions === 'object' && permissions !== null) {
      return Object.entries(permissions as Record<string, boolean>).filter(([, v]) => v === true).map(([k]) => k)
    }
    return []
  }, [permissions])
  const rawRole = useMemo(() => (user?.role || '').toLowerCase(), [user?.role])
  const isFullAccess = useMemo(() => ['super_admin', 'admin', 'proviseur'].includes(rawRole), [rawRole])
  const checkPerm = useCallback((perm: string | string[]): boolean => {
    if (isFullAccess) return true
    if (!rawPerms.length) return false
    if (Array.isArray(perm)) return perm.some(p => rawPerms.includes(p))
    return rawPerms.includes(perm)
  }, [rawPerms, isFullAccess])
  const canManage = checkPerm('fees_manage')
  const canWrite = checkPerm('fees_write') || canManage

  // ─── TABS ───
  const [activeTab, setActiveTab] = useState<'payments' | 'types' | 'structures' | 'reports'>('payments')

  // ─── CASCADE STATES ───
  const [institutionType, setInstitutionType] = useState<'lycee' | 'college'>('lycee')
  const [sections, setSections] = useState<Section[]>([])
  const [cycles, setCycles] = useState<Cycle[]>([])
  const [levels, setLevels] = useState<Level[]>([])
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [selectedSection, setSelectedSection] = useState<number | ''>('')
  const [selectedCycle, setSelectedCycle] = useState<number | ''>('')
  const [selectedLevel, setSelectedLevel] = useState<number | ''>('')
  const [selectedClass, setSelectedClass] = useState<number | ''>('')
  const [selectedYear, setSelectedYear] = useState<number | ''>('')
  const [years, setYears] = useState<any[]>([])
  const [refLoading, setRefLoading] = useState(false)

  // ─── DATA STATES ───
  const [students, setStudents] = useState<Student[]>([])
  const [feeTypes, setFeeTypes] = useState<FeeType[]>([])
  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([])
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStudents, setSelectedStudents] = useState<Set<number>>(new Set())

  // ─── MODALS ───
  const [showTypeModal, setShowTypeModal] = useState(false)
  const [showStructureModal, setShowStructureModal] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [showPayAllTypesModal, setShowPayAllTypesModal] = useState(false)
  const [editingType, setEditingType] = useState<FeeType | null>(null)
  const [editingStructure, setEditingStructure] = useState<FeeStructure | null>(null)

  // ─── LOAD YEARS ───
  const loadYears = useCallback(async () => {
    if (!userSchoolId) return
    try {
      const res = await schoolApi.getAcademicYears(userSchoolId)
      const arr = extractArray(res)
      setYears(arr)
      const selected = arr.find((y: any) => y.is_selected === true && y.school_id === userSchoolId)
      const current = arr.find((y: any) => y.is_current === true && y.school_id === userSchoolId)
      const found = selected || current || arr.find((y: any) => y.school_id === userSchoolId) || arr[0]
      if (found) setSelectedYear(found.id)
    } catch { }
  }, [userSchoolId])

  // ─── LOAD SECTIONS ───
  const loadSections = useCallback(async () => {
    if (!userSchoolId) { setSections([]); return }
    setRefLoading(true)
    try {
      let secData: any[] = []
      try {
        const res = await schoolApi.getSections(userSchoolId)
        secData = extractArray(res)
      } catch (e1) {
        try {
          const schools = await schoolApi.getSchools()
          const schoolsList = Array.isArray(schools) ? schools : (schools?.data || [])
          const activeSchool = schoolsList.find((s: any) => s.id === userSchoolId || s.status === 'active')
          if (activeSchool) {
            const res2 = await schoolApi.getSections(activeSchool.id)
            secData = extractArray(res2)
          }
        } catch (e2) { }
      }
      setSections(secData.map((s: any) => ({
        id: s.id, code: s.code || '',
        label_fr: s.label_fr || s.name || s.label || `Section ${s.id}`,
        label_en: s.label_en || s.name || s.label || `Section ${s.id}`,
        school_id: s.school_id
      })))
    } catch { setSections([]) } finally { setRefLoading(false) }
  }, [userSchoolId])

  // ─── LOAD REF DATA ───
  const loadRefData = useCallback(async () => {
    if (!userSchoolId) { setCycles([]); setLevels([]); setClasses([]); return }
    try {
      const [cycRes, levRes, clsRes] = await Promise.allSettled([
        cyclesApi.getAll(), levelsApi.getAll(), classesApi.getAll()
      ])
      const cycArr = cycRes.status === 'fulfilled' ? extractArray(cycRes.value) : []
      const levArr = levRes.status === 'fulfilled' ? extractArray(levRes.value) : []
      const clsArr = clsRes.status === 'fulfilled' ? extractArray(clsRes.value) : []
      setCycles(cycArr.map((c: any) => ({ id: c.id, section_id: c.section_id, code: c.code || '', label_fr: c.label_fr || c.name || c.label || `Cycle ${c.id}`, label_en: c.label_en || c.name || c.label || `Cycle ${c.id}`, order: c.order || 0 })))
      setLevels(levArr.map((l: any) => ({ id: l.id, cycle_id: l.cycle_id, code: l.code || '', label_fr: l.label_fr || l.name || l.label || `Niveau ${l.id}`, label_en: l.label_en || l.name || l.label || `Niveau ${l.id}`, order: l.order || 0 })))
      setClasses(clsArr.map((c: any) => ({ id: c.id, level_id: c.level_id, code: c.code || '', abbreviation: c.abbreviation || '', name: c.name || c.label || `Classe ${c.id}`, capacity: c.capacity, status: c.status })))
    } catch { }
  }, [userSchoolId])

  // ─── FILTRAGE CASCADÉ ───
  const filteredCycles = useMemo(() => {
    if (!selectedSection) return []
    return cycles.filter((c: Cycle) => c.section_id === Number(selectedSection))
  }, [cycles, selectedSection])
  const filteredLevels = useMemo(() => {
    if (!selectedCycle) {
      if (!selectedSection) return []
      return levels.filter((l: Level) => { const c = cycles.find((cc: Cycle) => cc.id === l.cycle_id); return c && c.section_id === Number(selectedSection) })
    }
    return levels.filter((l: Level) => l.cycle_id === Number(selectedCycle))
  }, [levels, cycles, selectedCycle, selectedSection])
  const filteredClasses = useMemo(() => {
    if (!selectedLevel) {
      if (!selectedCycle) return []
      return classes.filter((c: ClassItem) => { const lvl = levels.find((l: Level) => l.id === c.level_id); return lvl && lvl.cycle_id === Number(selectedCycle) })
    }
    return classes.filter((c: ClassItem) => c.level_id === Number(selectedLevel))
  }, [classes, levels, selectedLevel, selectedCycle])

  // ─── LOAD STUDENTS ───
  const loadStudents = useCallback(async (classId: number) => {
    if (!classId) { setStudents([]); return }
    try {
      const res = await studentsApi.getAll({ class_id: classId, school_id: userSchoolId, per_page: 500 })
      const arr = extractArray(res)
      setStudents(arr.map((s: any) => ({ id: s.id, first_name: s.first_name || '', last_name: s.last_name || '', matricule: s.matricule || s.registration_number || '', gender: s.gender || s.sex || '' })))
    } catch { setStudents([]) }
  }, [userSchoolId])

  // ─── LOAD FEE TYPES ───
  const loadFeeTypes = useCallback(async () => {
    try {
      const res = await feesApi.getFeeTypes({ institution_type: institutionType, is_active: true })
      const arr = extractArray(res)
      setFeeTypes(arr.sort((a: FeeType, b: FeeType) => (a.display_order || 0) - (b.display_order || 0)))
    } catch { setFeeTypes([]) }
  }, [institutionType])

  // ─── LOAD FEE STRUCTURES ───
  const loadFeeStructures = useCallback(async () => {
    if (!selectedYear || !userSchoolId) return
    try {
      const params: any = { academic_year_id: selectedYear }
      if (userSchoolId) params.school_id = userSchoolId
      if (selectedClass) params.class_id = selectedClass
      if (selectedLevel) params.level_id = selectedLevel
      const res = await feesApi.getFeeStructures(params)
      setFeeStructures(extractArray(res))
    } catch { setFeeStructures([]) }
  }, [selectedYear, selectedClass, selectedLevel, userSchoolId])

  // ─── LOAD PAYMENT STATUS ───
  const loadPaymentStatus = useCallback(async () => {
    if (!selectedClass || !selectedYear) { setPaymentStatus([]); return }
    setLoading(true)
    try {
      const res = await feesApi.getClassFeeStatus(selectedClass, selectedYear, undefined, institutionType)
      const arr = extractArray(res)
      setPaymentStatus(arr)
    } catch (e: any) {
      toast.error(e.message || 'Erreur chargement paiements')
      setPaymentStatus([])
    } finally { setLoading(false) }
  }, [selectedClass, selectedYear, institutionType])

  // ─── INIT ───
  useEffect(() => { if (userSchoolId) { loadYears(); loadSections(); loadRefData() } }, [userSchoolId, loadYears, loadSections, loadRefData])
  useEffect(() => { loadFeeTypes() }, [loadFeeTypes])

  // ─── CASCADE WATCHERS ───
  useEffect(() => { setSelectedCycle(''); setSelectedLevel(''); setSelectedClass(''); setStudents([]); setPaymentStatus([]) }, [selectedSection])
  useEffect(() => { setSelectedLevel(''); setSelectedClass(''); setStudents([]); setPaymentStatus([]) }, [selectedCycle])
  useEffect(() => { setSelectedClass(''); setStudents([]); setPaymentStatus([]) }, [selectedLevel])
  useEffect(() => {
    if (selectedClass) { loadStudents(selectedClass); loadPaymentStatus(); loadFeeStructures() }
  }, [selectedClass, loadStudents, loadPaymentStatus, loadFeeStructures])
  useEffect(() => {
    if (selectedClass && selectedYear) { loadPaymentStatus(); loadFeeStructures() }
  }, [selectedYear, loadPaymentStatus, loadFeeStructures, selectedClass])

  // ─── DERIVED DATA ───
  const feeTypesForClass = useMemo(() => {
    const typeIds = new Set(feeStructures.map(s => s.fee_type_id))
    return feeTypes.filter(ft => typeIds.has(ft.id))
  }, [feeTypes, feeStructures])

  const statusByStudent = useMemo(() => {
    const map: Record<number, PaymentStatus[]> = {}
    paymentStatus.forEach(p => { if (!map[p.student_id]) map[p.student_id] = []; map[p.student_id].push(p) })
    return map
  }, [paymentStatus])

  const statusByFeeType = useMemo(() => {
    const map: Record<number, PaymentStatus[]> = {}
    paymentStatus.forEach(p => { if (!map[p.fee_type_id]) map[p.fee_type_id] = []; map[p.fee_type_id].push(p) })
    return map
  }, [paymentStatus])

  const filteredStudents = useMemo(() => {
    if (!searchTerm) return students
    const term = searchTerm.toLowerCase()
    return students.filter(s => `${s.last_name} ${s.first_name}`.toLowerCase().includes(term) || (s.matricule || '').toLowerCase().includes(term))
  }, [students, searchTerm])

  // ─── CORRECTION : toNum() pour tous les calculs de totaux ───
  const summaryStats = useMemo(() => {
    const totalStudents = filteredStudents.length
    const totalDue = paymentStatus.reduce((sum, p) => sum + toNum(p.amount_due), 0)
    const totalPaid = paymentStatus.reduce((sum, p) => sum + toNum(p.amount_paid), 0)
    const totalRemaining = totalDue - totalPaid
    const rate = totalDue > 0 ? Math.round((totalPaid / totalDue) * 100) : 0
    return { totalStudents, totalDue, totalPaid, totalRemaining, rate }
  }, [paymentStatus, filteredStudents])

  // ─── HANDLERS ───
  const handleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) setSelectedStudents(new Set())
    else setSelectedStudents(new Set(filteredStudents.map(s => s.id)))
  }
  const toggleStudent = (id: number) => { const next = new Set(selectedStudents); if (next.has(id)) next.delete(id); else next.add(id); setSelectedStudents(next) }
  const handleMarkAllPaid = async (feeTypeId: number) => {
    if (!selectedClass || !selectedYear) return
    const studentIds = selectedStudents.size > 0 ? Array.from(selectedStudents) : filteredStudents.map(s => s.id)
    if (studentIds.length === 0) { toast.error(lang === 'fr' ? 'Aucun élève sélectionné' : 'No student selected'); return }
    try {
      const structure = feeStructures.find(s => s.fee_type_id === feeTypeId && s.class_id === selectedClass)
      const amount = structure ? toNum(structure.amount) : 0
      await feesApi.createBulkPaymentsByType({ student_ids: studentIds, class_id: selectedClass, fee_type_id: feeTypeId, academic_year_id: selectedYear, amount_paid: amount, payment_method: 'cash', status: 'paid' })
      toast.success(`${studentIds.length} élève(s) marqué(s) comme payé(s)`); loadPaymentStatus()
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Erreur') }
  }
  const handleMarkAllUnpaid = async (feeTypeId: number) => {
    if (!selectedClass || !selectedYear) return
    const studentIds = selectedStudents.size > 0 ? Array.from(selectedStudents) : filteredStudents.map(s => s.id)
    if (studentIds.length === 0) { toast.error(lang === 'fr' ? 'Aucun élève sélectionné' : 'No student selected'); return }
    try {
      await feesApi.createBulkPaymentsByType({ student_ids: studentIds, class_id: selectedClass, fee_type_id: feeTypeId, academic_year_id: selectedYear, amount_paid: 0, payment_method: 'cash', status: 'unpaid' })
      toast.success(`${studentIds.length} élève(s) marqué(s) comme non payé(s)`); loadPaymentStatus()
    } catch (e: any) { toast.error(e.response?.data?.detail || 'Erreur') }
  }

  // ─── PAYER TOUS LES TYPES POUR LES ÉLÈVES SÉLECTIONNÉS ───
  const [payAllTypesLoading, setPayAllTypesLoading] = useState(false)

  const handlePayAllTypesForSelected = () => {
    if (!selectedClass || !selectedYear) return
    const studentIds = selectedStudents.size > 0 ? Array.from(selectedStudents) : filteredStudents.map(s => s.id)
    if (studentIds.length === 0) { toast.error(lang === 'fr' ? 'Aucun élève sélectionné' : 'No student selected'); return }
    if (feeTypesForClass.length === 0) { toast.error(lang === 'fr' ? 'Aucun type de frais configuré' : 'No fee types configured'); return }
    setShowPayAllTypesModal(true)
  }

  const executePayAllTypes = async () => {
    if (!selectedClass || !selectedYear) return
    const studentIds = selectedStudents.size > 0 ? Array.from(selectedStudents) : filteredStudents.map(s => s.id)
    setPayAllTypesLoading(true)
    let successCount = 0
    let failCount = 0
    for (const ft of feeTypesForClass) {
      try {
        const structure = feeStructures.find(s => s.fee_type_id === ft.id && s.class_id === selectedClass)
        const amount = structure ? toNum(structure.amount) : 0
        await feesApi.createBulkPaymentsByType({
          student_ids: studentIds, class_id: selectedClass, fee_type_id: ft.id,
          academic_year_id: selectedYear, amount_paid: amount, payment_method: 'cash', status: 'paid'
        })
        successCount++
      } catch (e: any) {
        console.error(`[PayAllTypes] Erreur type ${ft.name}:`, e)
        failCount++
      }
    }
    setPayAllTypesLoading(false)
    setShowPayAllTypesModal(false)
    if (failCount > 0) {
      toast.success(`${successCount}/${feeTypesForClass.length} types payés, ${failCount} échecs`)
    } else {
      toast.success(`${txt.payAllTypesSuccess} ${studentIds.length} élève(s)`)
    }
    setSelectedStudents(new Set())
    loadPaymentStatus()
  }

  // ─── EXPORTS ───
  const exportCSV = (filterStatus?: 'paid' | 'unpaid' | 'partial') => {
    const feeTypeNames = feeTypesForClass.map(ft => ft.name)
    const headers = [txt.matricule, txt.student, txt.gender, ...feeTypeNames, `${txt.amountPaid} (Total)`, `${txt.remaining} (Total)`]
    const rows: string[][] = [headers]
    let grandTotalPaid = 0, grandTotalDue = 0

    filteredStudents.forEach(student => {
      const statuses = statusByStudent[student.id] || []
      const row: string[] = [student.matricule || '', `${student.last_name} ${student.first_name}`, student.gender || '']
      let totalPaid = 0, totalDue = 0
      feeTypesForClass.forEach(ft => {
        const st = statuses.find(s => s.fee_type_id === ft.id)
        if (st) {
          row.push(st.status === 'paid' ? 'PAYÉ' : st.status === 'partial' ? 'PARTIEL' : 'NON PAYÉ')
          totalPaid += toNum(st.amount_paid); totalDue += toNum(st.amount_due)
        } else { row.push('—') }
      })
      row.push(formatFCFA(totalPaid)); row.push(formatFCFA(totalDue - totalPaid))
      grandTotalPaid += totalPaid; grandTotalDue += totalDue
      if (!filterStatus) rows.push(row)
      else { const hasStatus = statuses.some(s => s.status === filterStatus); if (hasStatus) rows.push(row) }
    })
    // Ligne de totaux
    rows.push(['', '', txt.total, ...feeTypeNames.map(() => ''), formatFCFA(grandTotalPaid), formatFCFA(grandTotalDue - grandTotalPaid)])

    const suffix = filterStatus ? `-${filterStatus}` : '-all'
    const clsName = classes.find(c => c.id === selectedClass)?.name || 'classe'
    downloadCSV(`scolarite-${clsName}${suffix}.csv`, rows)
    toast.success('Export CSV généré')
  }

  // ─── EXPORT PDF via window.print() ───
  const handlePrintPDF = () => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) { toast.error('Veuillez autoriser les popups pour imprimer'); return }

    const clsName = classes.find(c => c.id === selectedClass)?.name || ''
    const yearName = years.find((y: any) => y.id === selectedYear)?.label || years.find((y: any) => y.id === selectedYear)?.name || ''
    const today = new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB')

    // Construire le HTML de la table
    let tableHTML = ''
    filteredStudents.forEach((student, idx) => {
      const statuses = statusByStudent[student.id] || []
      let totalPaid = 0, totalRemaining = 0
      let feeCells = ''
      feeTypesForClass.forEach(ft => {
        const st = statuses.find(s => s.fee_type_id === ft.id)
        const paid = st ? toNum(st.amount_paid) : 0
        const due = st ? toNum(st.amount_due) : 0
        totalPaid += paid; totalRemaining += (due - paid)
        const statusLabel = st ? (st.status === 'paid' ? 'PAYÉ' : st.status === 'partial' ? 'PARTIEL' : 'NON PAYÉ') : '—'
        const statusColor = st?.status === 'paid' ? '#059669' : st?.status === 'partial' ? '#d97706' : '#dc2626'
        feeCells += `<td style="border:1px solid #ddd;padding:6px 8px;text-align:center;font-size:11px;color:${statusColor};font-weight:600;">${statusLabel}<br/><span style="font-size:9px;color:#666;font-weight:400;">${formatFCFA(paid)}</span></td>`
      })
      tableHTML += `
        <tr style="background:${idx % 2 === 0 ? '#fff' : '#f9fafb'};">
          <td style="border:1px solid #ddd;padding:6px 8px;text-align:center;font-size:11px;">${idx + 1}</td>
          <td style="border:1px solid #ddd;padding:6px 8px;font-size:11px;font-family:monospace;">${student.matricule || '—'}</td>
          <td style="border:1px solid #ddd;padding:6px 8px;font-size:11px;font-weight:600;">${student.last_name} ${student.first_name}</td>
          <td style="border:1px solid #ddd;padding:6px 8px;text-align:center;font-size:11px;">${student.gender || '—'}</td>
          ${feeCells}
          <td style="border:1px solid #ddd;padding:6px 8px;text-align:right;font-size:11px;color:#059669;font-weight:600;">${formatFCFA(totalPaid)}</td>
          <td style="border:1px solid #ddd;padding:6px 8px;text-align:right;font-size:11px;color:#dc2626;font-weight:600;">${formatFCFA(totalRemaining)}</td>
        </tr>`
    })

    // Totaux globaux
    const totalDueAll = paymentStatus.reduce((s, p) => s + toNum(p.amount_due), 0)
    const totalPaidAll = paymentStatus.reduce((s, p) => s + toNum(p.amount_paid), 0)

    const feeHeaders = feeTypesForClass.map(ft => `<th style="border:1px solid #ddd;padding:8px;background:#1e3a5f;color:#fff;font-size:11px;text-align:center;">${ft.name}</th>`).join('')

    const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${txt.reportTitle}</title>
<style>
  @page { size: A4 landscape; margin: 15mm; }
  body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 20px; color: #333; }
  .header { text-align: center; border-bottom: 3px double #1e3a5f; padding-bottom: 15px; margin-bottom: 20px; }
  .header h1 { margin: 0; font-size: 18px; color: #1e3a5f; text-transform: uppercase; letter-spacing: 1px; }
  .header h2 { margin: 5px 0 0; font-size: 13px; color: #666; font-weight: 400; }
  .meta { display: flex; justify-content: space-between; margin-bottom: 15px; font-size: 12px; color: #555; }
  .meta strong { color: #1e3a5f; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th { background: #1e3a5f; color: #fff; padding: 8px; border: 1px solid #ddd; text-align: left; font-size: 11px; }
  td { border: 1px solid #ddd; padding: 6px 8px; }
  .totals { margin-top: 15px; display: flex; gap: 20px; justify-content: flex-end; font-size: 12px; }
  .totals .box { border: 2px solid #1e3a5f; padding: 10px 20px; border-radius: 6px; text-align: center; }
  .totals .box .label { font-size: 10px; color: #666; text-transform: uppercase; }
  .totals .box .value { font-size: 16px; font-weight: 700; color: #1e3a5f; }
  .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 10px; }
  @media print { .no-print { display: none; } }
</style>
</head>
<body>
  <div class="header">
    <h1>${txt.schoolName}</h1>
    <h2>${txt.schoolAddress}</h2>
    <h2 style="margin-top:10px;font-size:15px;color:#b45309;">★ ${txt.reportTitle} ★</h2>
  </div>
  <div class="meta">
    <div><strong>${txt.reportClass}:</strong> ${clsName}</div>
    <div><strong>${txt.reportYear}:</strong> ${yearName}</div>
    <div><strong>${txt.reportDate}:</strong> ${today}</div>
    <div><strong>${txt.totalStudents}:</strong> ${filteredStudents.length}</div>
  </div>
  <table>
    <thead>
      <tr>
        <th style="text-align:center;width:30px;">#</th>
        <th>${txt.matricule}</th>
        <th>${txt.student}</th>
        <th style="text-align:center;">${txt.gender}</th>
        ${feeHeaders}
        <th style="text-align:right;">${txt.amountPaid}</th>
        <th style="text-align:right;">${txt.remaining}</th>
      </tr>
    </thead>
    <tbody>${tableHTML}</tbody>
  </table>
  <div class="totals">
    <div class="box"><div class="label">${txt.totalDue}</div><div class="value">${formatFCFA(totalDueAll)}</div></div>
    <div class="box"><div class="label">${txt.totalPaid}</div><div class="value" style="color:#059669;">${formatFCFA(totalPaidAll)}</div></div>
    <div class="box"><div class="label">${txt.totalRemaining}</div><div class="value" style="color:#dc2626;">${formatFCFA(totalDueAll - totalPaidAll)}</div></div>
    <div class="box"><div class="label">${txt.paymentRate}</div><div class="value" style="color:#d97706;">${totalDueAll > 0 ? Math.round((totalPaidAll / totalDueAll) * 100) : 0}%</div></div>
  </div>
  <div class="footer">
    ${txt.schoolName} — ${txt.reportTitle} — ${today}
    <br/><span class="no-print" style="color:#999;">Imprimé depuis le système de gestion scolaire</span>
  </div>
  <div class="no-print" style="text-align:center;margin-top:20px;">
    <button onclick="window.print()" style="padding:10px 30px;background:#1e3a5f;color:#fff;border:none;border-radius:6px;font-size:14px;cursor:pointer;">${txt.print}</button>
  </div>
</body>
</html>`
    printWindow.document.write(html)
    printWindow.document.close()
    toast.success('Document PDF prêt. Utilisez Ctrl+P ou le bouton Imprimer.')
  }

  // ─── STATUS BADGE ───
  const StatusBadge = ({ status }: { status: string }) => {
    if (status === 'paid') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3" />{txt.paid}</span>
    if (status === 'partial') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700"><Clock className="w-3 h-3" />{txt.partial}</span>
    if (status === 'exempted') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"><AlertCircle className="w-3 h-3" />{txt.exempted}</span>
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><XCircle className="w-3 h-3" />{txt.unpaid}</span>
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER: PAYMENTS TAB
  // ═══════════════════════════════════════════════════════════════
  const renderPaymentsTab = () => (
    <div className="space-y-6">
      {/* Filters Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Filter className="w-4 h-4 text-blue-600" />{txt.filterByStructure}
          </h3>
          <button onClick={() => { setSelectedSection(''); setSelectedCycle(''); setSelectedLevel(''); setSelectedClass(''); setStudents([]); setPaymentStatus([]) }} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
            <X className="w-3 h-3" />{txt.resetFilters}
          </button>
        </div>
        {refLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4"><Loader2 className="w-4 h-4 animate-spin" />Chargement des sections...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.institutionType}</label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button onClick={() => setInstitutionType('lycee')} className={`px-4 py-2 text-sm font-medium transition-colors flex-1 ${institutionType === 'lycee' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{txt.lycee}</button>
                <button onClick={() => setInstitutionType('college')} className={`px-4 py-2 text-sm font-medium transition-colors flex-1 ${institutionType === 'college' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{txt.college}</button>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.academicYear}</label>
              <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : '')} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">{txt.loading}</option>{years.map((y: any) => (<option key={y.id} value={y.id}>{y.label || y.name}</option>))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.section}</label>
              <select value={selectedSection} onChange={(e) => { const v = e.target.value ? parseInt(e.target.value) : ''; setSelectedSection(v); setSelectedCycle(''); setSelectedLevel(''); setSelectedClass(''); setStudents([]); setPaymentStatus([]) }} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">{txt.selectSection}</option>{sections.map(s => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en} ({s.code})</option>))}
              </select>
              {sections.length === 0 && userSchoolId && <p className="text-xs text-amber-600 mt-1">Aucune section trouvée</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.cycle}</label>
              <select value={selectedCycle} onChange={(e) => { const v = e.target.value ? parseInt(e.target.value) : ''; setSelectedCycle(v); setSelectedLevel(''); setSelectedClass(''); setStudents([]); setPaymentStatus([]) }} disabled={!selectedSection || filteredCycles.length === 0} className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${(!selectedSection || filteredCycles.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200 text-gray-700'}`}>
                <option value="">{txt.selectCycle}</option>{filteredCycles.map(c => (<option key={c.id} value={c.id}>{lang === 'fr' ? c.label_fr : c.label_en}</option>))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.level}</label>
              <select value={selectedLevel} onChange={(e) => { const v = e.target.value ? parseInt(e.target.value) : ''; setSelectedLevel(v); setSelectedClass(''); setStudents([]); setPaymentStatus([]) }} disabled={!selectedCycle || filteredLevels.length === 0} className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${(!selectedCycle || filteredLevels.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200 text-gray-700'}`}>
                <option value="">{txt.selectLevel}</option>{filteredLevels.map(l => (<option key={l.id} value={l.id}>{lang === 'fr' ? l.label_fr : l.label_en}</option>))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{txt.class}</label>
              <select value={selectedClass} onChange={(e) => { const v = e.target.value ? parseInt(e.target.value) : ''; setSelectedClass(v) }} disabled={!selectedLevel || filteredClasses.length === 0} className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${(!selectedLevel || filteredClasses.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white border-gray-200 text-gray-700'}`}>
                <option value="">{txt.selectClass}</option>{filteredClasses.map(c => (<option key={c.id} value={c.id}>{c.name} ({c.abbreviation})</option>))}
              </select>
            </div>
          </div>
        )}
        {(selectedSection || selectedCycle || selectedLevel || selectedClass) && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500">{txt.activeFilters}</span>
            {selectedSection && (<span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center gap-1">{txt.section}: {sections.find(s => s.id === Number(selectedSection))?.label_fr}<button onClick={() => { setSelectedSection(''); setSelectedCycle(''); setSelectedLevel(''); setSelectedClass('') }} className="hover:text-blue-900"><X className="w-3 h-3" /></button></span>)}
            {selectedCycle && (<span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium flex items-center gap-1">{txt.cycle}: {filteredCycles.find(c => c.id === Number(selectedCycle))?.label_fr}<button onClick={() => { setSelectedCycle(''); setSelectedLevel(''); setSelectedClass('') }} className="hover:text-purple-900"><X className="w-3 h-3" /></button></span>)}
            {selectedLevel && (<span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1">{txt.level}: {filteredLevels.find(l => l.id === Number(selectedLevel))?.label_fr}<button onClick={() => { setSelectedLevel(''); setSelectedClass('') }} className="hover:text-amber-900"><X className="w-3 h-3" /></button></span>)}
            {selectedClass && (<span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1">{txt.class}: {filteredClasses.find(c => c.id === Number(selectedClass))?.name}<button onClick={() => setSelectedClass('')} className="hover:text-emerald-900"><X className="w-3 h-3" /></button></span>)}
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {selectedClass && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">{txt.totalStudents}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{summaryStats.totalStudents}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">{txt.totalDue}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatFCFA(summaryStats.totalDue)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">{txt.totalPaid}</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">{formatFCFA(summaryStats.totalPaid)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">{txt.totalRemaining}</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{formatFCFA(summaryStats.totalRemaining)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">{txt.paymentRate}</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 bg-gray-100 rounded-full h-2.5"><div className="bg-emerald-500 h-2.5 rounded-full transition-all" style={{ width: `${summaryStats.rate}%` }} /></div>
              <span className="text-sm font-bold text-gray-900">{summaryStats.rate}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      {selectedClass && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" placeholder={txt.searchStudent} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 w-64" />
            </div>
            <button onClick={handleSelectAll} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
              {selectedStudents.size === filteredStudents.length ? txt.deselectAll : txt.selectAll}
            </button>
            {selectedStudents.size > 0 && <span className="text-sm text-primary-600 font-medium">{selectedStudents.size} sélectionné(s)</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => exportCSV('paid')} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"><FileDown className="w-4 h-4" />{txt.exportPaid}</button>
            <button onClick={() => exportCSV('unpaid')} className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm font-medium text-red-700 hover:bg-red-100 transition-colors"><FileDown className="w-4 h-4" />{txt.exportUnpaid}</button>
            <button onClick={() => exportCSV()} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"><FileSpreadsheet className="w-4 h-4" />{txt.exportAll}</button>
            <button onClick={handlePrintPDF} className="flex items-center gap-2 px-3 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"><Printer className="w-4 h-4" />{txt.exportPDF}</button>
            {canWrite && (
              <>
                <button
                  onClick={handlePayAllTypesForSelected}
                  disabled={filteredStudents.length === 0}
                  className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  title={txt.payAllTypesDesc}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {txt.payAllTypes}
                  {selectedStudents.size > 0 && <span className="ml-1 text-xs bg-emerald-800 px-1.5 py-0.5 rounded-full">{selectedStudents.size}</span>}
                </button>
                <button onClick={() => setShowBulkModal(true)} disabled={selectedStudents.size === 0} className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"><Banknote className="w-4 h-4" />{txt.savePayments}</button>
              </>
            )}
            <button onClick={loadPaymentStatus} className="p-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
          </div>
        </div>
      )}

      {/* Students Table */}
      {selectedClass ? (
        loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <RefreshCw className="w-8 h-8 text-gray-300 animate-spin mx-auto mb-3" />
            <p className="text-gray-500">{txt.loading}</p>
          </div>
        ) : feeTypesForClass.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">{txt.noFeeStructures}</p>
            <p className="text-sm text-gray-400 mt-1">{lang === 'fr' ? "Créez d'abord les structures de frais dans l'onglet Structures" : 'Create fee structures first in the Structures tab'}</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">{txt.noStudents}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-3 py-3 text-left w-10">
                      <input type="checkbox" checked={selectedStudents.size === filteredStudents.length && filteredStudents.length > 0} onChange={handleSelectAll} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                    </th>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700">#</th>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700">{txt.matricule}</th>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700">{txt.student}</th>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700">{txt.gender}</th>
                    {feeTypesForClass.map(ft => (
                      <th key={ft.id} className="px-3 py-3 text-center font-semibold text-gray-700 min-w-[140px]">
                        <div className="flex flex-col items-center gap-1">
                          <span>{ft.name}</span>
                          {canWrite && (
                            <div className="flex gap-1" title={selectedStudents.size > 0 ? `${selectedStudents.size} élève(s) sélectionné(s)` : 'Tous les élèves visibles'}>
                              <button onClick={() => handleMarkAllPaid(ft.id)} className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200">✓</button>
                              <button onClick={() => handleMarkAllUnpaid(ft.id)} className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded hover:bg-red-200">✗</button>
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="px-3 py-3 text-right font-semibold text-gray-700">{txt.amountPaid}</th>
                    <th className="px-3 py-3 text-right font-semibold text-gray-700">{txt.remaining}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredStudents.map((student, idx) => {
                    const statuses = statusByStudent[student.id] || []
                    // ─── CORRECTION : toNum() pour les totaux par élève ───
                    const totalPaid = statuses.reduce((s, p) => s + toNum(p.amount_paid), 0)
                    const totalRemaining = statuses.reduce((s, p) => s + toNum(p.remaining), 0)
                    return (
                      <tr key={student.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={selectedStudents.has(student.id)} onChange={() => toggleStudent(student.id)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                        </td>
                        <td className="px-3 py-2.5 text-gray-500">{idx + 1}</td>
                        <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{student.matricule || '—'}</td>
                        <td className="px-3 py-2.5 font-medium text-gray-900">{student.last_name} {student.first_name}</td>
                        <td className="px-3 py-2.5 text-gray-500 text-xs">{student.gender || '—'}</td>
                        {feeTypesForClass.map(ft => {
                          const st = statuses.find(s => s.fee_type_id === ft.id)
                          return (
                            <td key={ft.id} className="px-3 py-2.5 text-center">
                              {st ? (
                                <div className="flex flex-col items-center gap-1">
                                  <StatusBadge status={st.status} />
                                  {st.status !== 'unpaid' && <span className="text-[10px] text-gray-400">{formatFCFA(st.amount_paid)}</span>}
                                </div>
                              ) : (<span className="text-gray-300">—</span>)}
                            </td>
                          )
                        })}
                        <td className="px-3 py-2.5 text-right font-medium text-emerald-600">{formatFCFA(totalPaid)}</td>
                        <td className="px-3 py-2.5 text-right font-medium text-red-600">{formatFCFA(totalRemaining)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <School className="w-12 h-12 text-gray-200 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">{txt.pleaseSelectClass}</p>
          <p className="text-sm text-gray-400 mt-1">{lang === 'fr' ? 'Utilisez les filtres ci-dessus pour sélectionner une classe' : 'Use the filters above to select a class'}</p>
        </div>
      )}
    </div>
  )

  // ═══════════════════════════════════════════════════════════════
  // RENDER: FEE TYPES TAB
  // ═══════════════════════════════════════════════════════════════
  const renderTypesTab = () => {
    const filteredTypes = feeTypes.filter(ft => ft.institution_type === institutionType)
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              <button onClick={() => setInstitutionType('lycee')} className={`px-4 py-2 text-sm font-medium ${institutionType === 'lycee' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{txt.lycee}</button>
              <button onClick={() => setInstitutionType('college')} className={`px-4 py-2 text-sm font-medium ${institutionType === 'college' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{txt.college}</button>
            </div>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <button onClick={async () => { try { await feesApi.seedDefaultTypes(institutionType); toast.success(txt.seedSuccess); loadFeeTypes() } catch (e: any) { toast.error(e.message || 'Erreur') } }} className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm font-medium text-amber-700 hover:bg-amber-100 transition-colors"><RefreshCw className="w-4 h-4" />{txt.seedDefaults}</button>
              <button onClick={() => { setEditingType(null); setShowTypeModal(true) }} className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"><Plus className="w-4 h-4" />{txt.createFeeType}</button>
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{txt.code}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{txt.name}</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">{txt.category}</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">{txt.mandatory}</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">{txt.active}</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">{txt.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredTypes.map(ft => (
                <tr key={ft.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{ft.code}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{ft.name}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{ft.category || '—'}</td>
                  <td className="px-4 py-3 text-center">{ft.is_mandatory ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />}</td>
                  <td className="px-4 py-3 text-center">{ft.is_active ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-red-300 mx-auto" />}</td>
                  <td className="px-4 py-3 text-right">
                    {canManage && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { setEditingType(ft); setShowTypeModal(true) }} className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={async () => { if (!window.confirm(txt.confirmDelete)) return; try { await feesApi.deleteFeeType(ft.id); toast.success('Supprimé'); loadFeeTypes() } catch (e: any) { toast.error(e.message || 'Erreur') } }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filteredTypes.length === 0 && (<tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">{txt.noData}</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER: STRUCTURES TAB
  // ═══════════════════════════════════════════════════════════════
  const renderStructuresTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{txt.tabStructures}</h3>
        {canManage && (
          <button onClick={() => { setEditingStructure(null); setShowStructureModal(true) }} className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"><Plus className="w-4 h-4" />{txt.createStructure}</button>
        )}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 text-left font-semibold text-gray-700">{txt.feeType}</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">{txt.level}</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">{txt.class}</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">{txt.amount}</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">{txt.dueDate}</th>
              <th className="px-4 py-3 text-center font-semibold text-gray-700">{txt.active}</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">{txt.actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {feeStructures.map(s => (
              <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 font-medium text-gray-900">{s.fee_type?.name || feeTypes.find(ft => ft.id === s.fee_type_id)?.name || '—'}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{levels.find(l => l.id === s.level_id)?.label_fr || '—'}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{classes.find(c => c.id === s.class_id)?.name || '—'}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-900">{formatFCFA(s.amount)}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{s.due_date ? new Date(s.due_date).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="px-4 py-3 text-center">{s.is_active !== false ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-gray-300 mx-auto" />}</td>
                <td className="px-4 py-3 text-right">
                  {canManage && (
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditingStructure(s); setShowStructureModal(true) }} className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={async () => { if (!window.confirm(txt.confirmDelete)) return; try { await feesApi.deleteFeeStructure(s.id); toast.success('Supprimé'); loadFeeStructures() } catch (e: any) { toast.error(e.message || 'Erreur') } }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {feeStructures.length === 0 && (<tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">{txt.noData}</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  )

  // ═══════════════════════════════════════════════════════════════
  // RENDER: REPORTS TAB
  // ═══════════════════════════════════════════════════════════════
  const renderReportsTab = () => {
    // ─── CORRECTION : toNum() pour les totaux des rapports ───
    const byFee = feeTypesForClass.map(ft => {
      const items = statusByFeeType[ft.id] || []
      const paid = items.filter(i => i.status === 'paid').length
      const unpaid = items.filter(i => i.status === 'unpaid').length
      const partial = items.filter(i => i.status === 'partial').length
      const totalDue = items.reduce((s, i) => s + toNum(i.amount_due), 0)
      const totalPaid = items.reduce((s, i) => s + toNum(i.amount_paid), 0)
      return { ...ft, paid, unpaid, partial, totalDue, totalPaid }
    })
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">{txt.classSummary}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {byFee.map(ft => (
            <div key={ft.id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900">{ft.name}</h4>
                <span className="text-xs font-mono text-gray-400">{ft.code}</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-500">{txt.totalDue}</span><span className="font-medium">{formatFCFA(ft.totalDue)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">{txt.totalPaid}</span><span className="font-medium text-emerald-600">{formatFCFA(ft.totalPaid)}</span></div>
                <div className="w-full bg-gray-100 rounded-full h-2 mt-2"><div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${ft.totalDue > 0 ? Math.round((ft.totalPaid / ft.totalDue) * 100) : 0}%` }} /></div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-emerald-600">{ft.paid} {txt.paidCount}</span>
                  <span className="text-amber-600">{ft.partial} {txt.partialCount}</span>
                  <span className="text-red-600">{ft.unpaid} {txt.unpaidCount}</span>
                </div>
              </div>
            </div>
          ))}
          {byFee.length === 0 && (<div className="md:col-span-3 bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">{txt.noData}</div>)}
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Wallet className="w-7 h-7 text-primary-600" />{txt.title}</h1>
          <p className="text-sm text-gray-500 mt-1">{txt.subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-1 bg-white rounded-xl border border-gray-200 p-1 w-fit">
        {([
          { key: 'payments' as const, label: txt.tabPayments, icon: Banknote },
          { key: 'types' as const, label: txt.tabTypes, icon: Layers },
          { key: 'structures' as const, label: txt.tabStructures, icon: Calculator },
          { key: 'reports' as const, label: txt.tabReports, icon: BarChart3 },
        ]).map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.key ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'payments' && renderPaymentsTab()}
      {activeTab === 'types' && renderTypesTab()}
      {activeTab === 'structures' && renderStructuresTab()}
      {activeTab === 'reports' && renderReportsTab()}
      {/* ═══ MODAL CONFIRMATION PAYER TOUS LES TYPES ═══ */}
      {showPayAllTypesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => !payAllTypesLoading && setShowPayAllTypesModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">{txt.payAllTypes}</h3>
                <p className="text-emerald-100 text-sm">{lang === 'fr' ? 'Confirmation de paiement multiple' : 'Multiple payment confirmation'}</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Info élèves */}
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-center gap-3">
                <Users className="w-8 h-8 text-emerald-600" />
                <div>
                  <p className="text-sm font-semibold text-emerald-800">
                    {selectedStudents.size > 0 ? selectedStudents.size : filteredStudents.length} {lang === 'fr' ? 'élève(s)' : 'student(s)'}
                  </p>
                  <p className="text-xs text-emerald-600">
                    {selectedStudents.size > 0 ? txt.selectedOnly : txt.allVisible}
                  </p>
                </div>
              </div>
              {/* Liste des types */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">{lang === 'fr' ? 'Types de frais à payer' : 'Fee types to pay'}</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {feeTypesForClass.map(ft => {
                    const structure = feeStructures.find(s => s.fee_type_id === ft.id && s.class_id === selectedClass)
                    const amount = structure ? toNum(structure.amount) : 0
                    return (
                      <div key={ft.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-sm text-gray-700">{ft.name}</span>
                        <span className="text-sm font-semibold text-emerald-600">{formatFCFA(amount)}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
              {/* Total */}
              <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">{lang === 'fr' ? 'Total par élève' : 'Total per student'}</span>
                <span className="text-lg font-bold text-emerald-600">
                  {formatFCFA(feeTypesForClass.reduce((sum, ft) => {
                    const s = feeStructures.find(st => st.fee_type_id === ft.id && st.class_id === selectedClass)
                    return sum + (s ? toNum(s.amount) : 0)
                  }, 0))}
                </span>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setShowPayAllTypesModal(false)}
                disabled={payAllTypesLoading}
                className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {txt.cancel}
              </button>
              <button
                onClick={executePayAllTypes}
                disabled={payAllTypesLoading}
                className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg text-sm font-medium hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-md shadow-emerald-200 disabled:opacity-50 flex items-center gap-2"
              >
                {payAllTypesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {lang === 'fr' ? 'Confirmer le paiement' : 'Confirm payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      <FeeTypeModal show={showTypeModal} onClose={() => setShowTypeModal(false)} editingType={editingType} institutionType={institutionType} txt={txt} onSaved={loadFeeTypes} />
      <FeeStructureModal show={showStructureModal} onClose={() => setShowStructureModal(false)} editingStructure={editingStructure} feeTypes={feeTypes} levels={levels} classes={classes} selectedLevel={selectedLevel} selectedClass={selectedClass} selectedYear={selectedYear} institutionType={institutionType} userSchoolId={userSchoolId} txt={txt} onSaved={loadFeeStructures} />
      <BulkPaymentModal show={showBulkModal} onClose={() => setShowBulkModal(false)} selectedStudents={selectedStudents} feeTypesForClass={feeTypesForClass} selectedClass={selectedClass} selectedYear={selectedYear} txt={txt} onSaved={() => { setSelectedStudents(new Set()); loadPaymentStatus() }} />
    </div>
  )
}